import { Component, OnChanges, OnInit, computed, inject, input, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { SelectModule } from 'primeng/select';
import { InputTextModule } from 'primeng/inputtext';
import { LocalizationService } from '../../../core/localization/localization.service';
import { AinCanAccessDirective } from '../../../shared/security/access.directive';
import { WorkflowApiCallOperatorConfig, WorkflowApiConnectorCatalogItem } from '../workflow-catalog.models';
import { WorkflowApiService } from '../workflow-api.service';
import { parseConfigJson, parseStringMapping, stringifyConfigJson } from './workflow-operator-config.utils';
import { WorkflowVariableMappingGridComponent } from './workflow-variable-mapping-grid.component';
import { WorkflowRetryPolicyFormComponent } from './workflow-retry-policy-form.component';

@Component({
  standalone: true,
  selector: 'ain-workflow-api-call-operator-panel',
  imports: [
    FormsModule,
    SelectModule,
    InputTextModule,
    AinCanAccessDirective,
    WorkflowVariableMappingGridComponent,
    WorkflowRetryPolicyFormComponent
  ],
  template: `
    <section class="operator-panel" ainCanAccess [ainCanAccessPermission]="'workflow.connectors.catalog.read'">
      <h5>{{ t('workflow.designer.operators.apiCall.title') }}</h5>
      <p class="hint">{{ t('workflow.designer.operators.apiCall.noUrlHint') }}</p>

      <label>{{ t('workflow.designer.operators.apiCall.connectorKey') }}</label>
      <p-select
        [options]="connectorOptions()"
        optionLabel="label"
        optionValue="value"
        [(ngModel)]="model.connectorKey"
        (ngModelChange)="onConnectorChanged()"
        [filter]="true"
        appendTo="body"
        [placeholder]="t('common.select')" />

      <label>{{ t('workflow.designer.operators.apiCall.operationKey') }}</label>
      <p-select
        [options]="operationOptions()"
        optionLabel="label"
        optionValue="value"
        [(ngModel)]="model.operationKey"
        (ngModelChange)="emitConfig()"
        [filter]="true"
        appendTo="body"
        [placeholder]="t('common.select')" />

      @if (selectedOperation(); as operation) {
        <p class="operation-meta">{{ operation.httpMethod }} {{ operation.pathTemplate }}</p>
      }

      <label>{{ t('workflow.designer.operators.apiCall.timeoutSeconds') }}</label>
      <input pInputText type="number" min="1" max="120" [(ngModel)]="model.timeoutSeconds" (ngModelChange)="emitConfig()" />

      <h6>{{ t('workflow.designer.operators.inputMapping') }}</h6>
      <ain-workflow-variable-mapping-grid
        [mapping]="model.inputMapping ?? {}"
        (mappingChange)="onInputMappingChanged($event)" />

      <h6>{{ t('workflow.designer.operators.outputMapping') }}</h6>
      <ain-workflow-variable-mapping-grid
        [mapping]="model.outputMapping ?? {}"
        (mappingChange)="onOutputMappingChanged($event)" />

      <h6>{{ t('workflow.designer.operators.retry.title') }}</h6>
      <ain-workflow-retry-policy-form
        [retryPolicy]="model.retryPolicy"
        (retryPolicyChange)="onRetryPolicyChanged($event)" />
    </section>
  `,
  styles: [`
    .operator-panel { display: flex; flex-direction: column; gap: 8px; margin-top: 8px; }
    .operator-panel h5, .operator-panel h6 { margin: 8px 0 4px; font-size: 13px; color: #334155; }
    .operator-panel label { font-size: 12px; color: #475569; }
    .hint, .operation-meta { font-size: 12px; color: #64748b; margin: 0; }
  `]
})
export class WorkflowApiCallOperatorPanelComponent implements OnInit, OnChanges {
  private readonly api = inject(WorkflowApiService);
  private readonly localization = inject(LocalizationService);

  readonly configJson = input('{}');
  readonly configJsonChange = output<string>();

  readonly catalog = signal<WorkflowApiConnectorCatalogItem[]>([]);
  model: WorkflowApiCallOperatorConfig = { timeoutSeconds: 30 };

  readonly connectorOptions = computed(() =>
    this.catalog().map(item => ({ label: `${item.connectorKey} — ${item.displayName}`, value: item.connectorKey })));

  readonly operationOptions = computed(() => {
    const connectorKey = this.model.connectorKey;
    if (!connectorKey) return [];
    const connector = this.catalog().find(item => item.connectorKey === connectorKey);
    return (connector?.operations ?? []).map(item => ({
      label: `${item.operationKey} — ${item.displayName}`,
      value: item.operationKey
    }));
  });

  ngOnInit(): void {
    this.api.getConnectorsCatalog().subscribe({
      next: items => this.catalog.set(items),
      error: () => this.catalog.set([])
    });
    this.loadFromConfig(this.configJson());
  }

  ngOnChanges(): void {
    this.loadFromConfig(this.configJson());
  }

  selectedOperation() {
    const connector = this.catalog().find(item => item.connectorKey === this.model.connectorKey);
    return connector?.operations.find(item => item.operationKey === this.model.operationKey);
  }

  onConnectorChanged(): void {
    this.model.operationKey = undefined;
    this.emitConfig();
  }

  onInputMappingChanged(mapping: Record<string, string>): void {
    this.model.inputMapping = mapping;
    this.emitConfig();
  }

  onOutputMappingChanged(mapping: Record<string, string>): void {
    this.model.outputMapping = mapping;
    this.emitConfig();
  }

  onRetryPolicyChanged(retryPolicy: WorkflowApiCallOperatorConfig['retryPolicy']): void {
    this.model.retryPolicy = retryPolicy;
    this.emitConfig();
  }

  emitConfig(): void {
    this.configJsonChange.emit(stringifyConfigJson(this.model));
  }

  private loadFromConfig(raw: string): void {
    this.model = parseConfigJson<WorkflowApiCallOperatorConfig>(raw, {
      connectorKey: '',
      operationKey: '',
      timeoutSeconds: 30,
      inputMapping: {},
      outputMapping: {}
    });
    this.model.inputMapping = parseStringMapping(this.model.inputMapping);
    this.model.outputMapping = parseStringMapping(this.model.outputMapping);
    if (!this.model.timeoutSeconds) {
      this.model.timeoutSeconds = 30;
    }
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
